import { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, ShoppingBag, X, Heart, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import CalendarPromoBanner from '../components/CalendarPromoBanner';
import PostSubscribeForm from '../components/PostSubscribeForm';

const LIKED_POSTS_KEY = 'obxbb_liked_posts';
const LIKED_COMMENTS_KEY = 'obxbb_liked_comments';
const COMMENTER_KEY = 'obxbb_commenter';

// There's no reader login on this site, so this is the lightweight stand-in:
// remember a commenter's name/email in their browser so returning visitors
// on the same device don't have to retype it every time.
function getSavedCommenter() {
  try {
    const saved = JSON.parse(localStorage.getItem(COMMENTER_KEY) || '{}');
    return { name: saved.name || '', email: saved.email || '' };
  } catch (e) {
    return { name: '', email: '' };
  }
}

function saveCommenter(name, email) {
  try {
    localStorage.setItem(COMMENTER_KEY, JSON.stringify({ name, email }));
  } catch (e) {
    // Private browsing / storage disabled — not remembering is fine, just no worse than before.
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PostView() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageTitle, setSelectedImageTitle] = useState('');
  const [selectedType, setSelectedType] = useState('paper');
  const [selectedSize, setSelectedSize] = useState('');
  const [pricing, setPricing] = useState({});
  const [typeNames, setTypeNames] = useState({});

  // Locking background scroll while the print modal is open avoids a known
  // mobile Chrome/Android quirk where a `position: fixed` overlay opened
  // over a still-scrollable page renders anchored to the scroll position
  // it opened at, instead of the actual viewport.
  useEffect(() => {
    if (!showPrintModal) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [showPrintModal]);

  // Likes & comments
  const [likesCount, setLikesCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentForm, setCommentForm] = useState(() => ({ ...getSavedCommenter(), text: '', website: '' }));
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const commentBoxRef = useRef(null);
  const [likedComments, setLikedComments] = useState([]);
  const [commentLikeLoadingId, setCommentLikeLoadingId] = useState(null);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyForm, setReplyForm] = useState(() => ({ ...getSavedCommenter(), text: '', website: '' }));
  const [replySubmitting, setReplySubmitting] = useState(false);

  // Drives the print modal's layout: side-by-side on desktop, stacked and
  // scrollable on phones — the modal has no CSS-class hook of its own, so
  // this branches the inline styles directly rather than fighting them.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    fetchPost();
    fetchPricing();
  }, [slug]);

  // React Router doesn't auto-scroll to a URL hash on its own, and the
  // #comments element doesn't exist until comments finish loading — so jump
  // to it manually once that content is actually on the page.
  // #leave-comment (the "Leave a comment" link in the emails) also puts the
  // cursor in the comment box.
  useEffect(() => {
    if (commentsLoading) return;
    if (location.hash === '#comments') {
      document.getElementById('comments')?.scrollIntoView({ behavior: 'smooth' });
    } else if (location.hash === '#leave-comment') {
      goToCommentBox();
    } else if (location.hash.startsWith('#comment-')) {
      // "See the conversation" in a reply email
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location.hash, commentsLoading]);

  // Scroll to the comment form and focus the box once the smooth scroll has
  // mostly finished (focusing straight away would jump instead of scroll).
  const goToCommentBox = () => {
    document.getElementById('comments')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => commentBoxRef.current?.focus({ preventScroll: true }), 500);
  };

  const fetchPost = async () => {
    let switchingAddress = false;
    try {
      const response = await axios.get(`/public/posts/${slug}`);
      // Reached by an old address (the title was changed after the link was
      // sent) or by id — switch to the post's current address.
      if (response.data.slug && response.data.slug !== slug) {
        navigate(`/post/${response.data.slug}${location.hash}`, { replace: true });
        switchingAddress = true;
        return;
      }
      setPost(response.data);
      setLikesCount(response.data.likes_count || 0);
      const likedPosts = JSON.parse(localStorage.getItem(LIKED_POSTS_KEY) || '[]');
      setLiked(likedPosts.includes(response.data.id));
      setLikedComments(JSON.parse(localStorage.getItem(LIKED_COMMENTS_KEY) || '[]'));
      fetchComments(response.data.id);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      setError('Post not found');
    } finally {
      if (!switchingAddress) setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`/public/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLike = async () => {
    if (liked || likeLoading || !post) return;
    setLikeLoading(true);
    try {
      const response = await axios.post(`/public/posts/${post.id}/like`);
      setLikesCount(response.data.likes_count);
      setLiked(true);
      const likedPosts = JSON.parse(localStorage.getItem(LIKED_POSTS_KEY) || '[]');
      localStorage.setItem(LIKED_POSTS_KEY, JSON.stringify([...likedPosts, post.id]));
    } catch (error) {
      console.error('Failed to like post:', error);
      toast.error('Failed to like post. Please try again.');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.text.trim()) {
      toast.error('Please enter your name and a comment');
      return;
    }
    setCommentSubmitting(true);
    try {
      const response = await axios.post(`/public/posts/${post.id}/comments`, {
        name: commentForm.name.trim(),
        text: commentForm.text.trim(),
        email: commentForm.email.trim() || null,
        website: commentForm.website
      });
      setComments((prev) => [...prev, response.data]);
      saveCommenter(commentForm.name.trim(), commentForm.email.trim());
      setCommentForm((prev) => ({ ...prev, text: '', website: '' }));
      toast.success('Comment posted');
    } catch (error) {
      console.error('Failed to post comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to post comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleReplySubmit = async (e, parentId) => {
    e.preventDefault();
    if (!replyForm.name.trim() || !replyForm.text.trim()) {
      toast.error('Please enter your name and a reply');
      return;
    }
    setReplySubmitting(true);
    try {
      const response = await axios.post(`/public/posts/${post.id}/comments`, {
        name: replyForm.name.trim(),
        text: replyForm.text.trim(),
        email: replyForm.email.trim() || null,
        website: replyForm.website,
        parent_id: parentId
      });
      setComments((prev) => [...prev, response.data]);
      saveCommenter(replyForm.name.trim(), replyForm.email.trim());
      setReplyForm({ name: '', email: '', text: '', website: '' });
      setReplyingToId(null);
      toast.success('Reply posted');
    } catch (error) {
      console.error('Failed to post reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to post reply. Please try again.');
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (likedComments.includes(commentId) || commentLikeLoadingId) return;
    setCommentLikeLoadingId(commentId);
    try {
      const response = await axios.post(`/public/comments/${commentId}/like`);
      setComments((prev) => prev.map((c) => (
        c.id === commentId ? { ...c, likes_count: response.data.likes_count } : c
      )));
      const updated = [...likedComments, commentId];
      setLikedComments(updated);
      localStorage.setItem(LIKED_COMMENTS_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to like comment:', error);
      toast.error('Failed to like comment. Please try again.');
    } finally {
      setCommentLikeLoadingId(null);
    }
  };

  const formatCommentDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/prints/pricing`);
      setPricing(response.data.pricing);
      setTypeNames(response.data.type_names);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const openPrintModal = (imageUrl, imageTitle) => {
    setSelectedImage(imageUrl);
    setSelectedImageTitle(imageTitle);
    setSelectedType('paper');
    // Auto-select first available size for paper type
    if (pricing['paper']) {
      setSelectedSize(Object.keys(pricing['paper'])[0]);
    } else {
      setSelectedSize('');
    }
    setShowPrintModal(true);
  };

  const handleCheckout = async () => {
    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }
    
    setCheckoutLoading(true);
    
    try {
      const price = pricing[selectedType]?.[selectedSize];
      const response = await axios.post(`${BACKEND_URL}/api/prints/checkout`, {
        print_id: `post-${post.id}-${Date.now()}`,
        print_title: selectedImageTitle || post.title,
        print_type: selectedType,
        size: selectedSize,
        price: price,
        image_url: selectedImage,
        origin_url: window.location.origin,
        source: 'post'
      });
      
      const checkoutUrl = response.data.checkout_url || response.data.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error('Failed to get checkout URL');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    if (pricing[type]) {
      setSelectedSize(Object.keys(pricing[type])[0]);
    }
  };

  const getPrice = () => {
    if (pricing[selectedType] && pricing[selectedType][selectedSize]) {
      return pricing[selectedType][selectedSize];
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="public-site">
        <div className="loading-spinner" style={{ margin: '100px auto' }}></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="public-site">
        <div className="public-container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h2>Post Not Found</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>The post you're looking for doesn't exist.</p>
          <Link to="/" className="btn btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const rootComments = comments.filter((c) => !c.parent_id);
  const repliesByParentId = comments.reduce((acc, c) => {
    if (c.parent_id) {
      (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
    }
    return acc;
  }, {});

  const renderLikeButton = () => (
    <button
      onClick={handleLike}
      disabled={likeLoading}
      data-testid="like-button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        border: `1px solid ${liked ? '#e0245e' : '#ddd'}`,
        borderRadius: '20px',
        background: liked ? '#fdeef2' : '#fff',
        color: liked ? '#e0245e' : '#666',
        fontSize: '14px',
        cursor: liked || likeLoading ? 'default' : 'pointer'
      }}
    >
      <Heart size={16} fill={liked ? '#e0245e' : 'none'} />
      {likesCount > 0 ? likesCount : 'Like'}
    </button>
  );

  const renderComment = (comment, depth = 0) => {
    const isLiked = likedComments.includes(comment.id);
    const replies = repliesByParentId[comment.id] || [];
    return (
      <div
        key={comment.id}
        id={`comment-${comment.id}`}
        style={{
          padding: '16px 0',
          borderBottom: depth === 0 ? '1px solid #f0f0f0' : 'none',
          marginLeft: depth > 0 ? '28px' : 0
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>
            {comment.name}
            {comment.is_author && (
              <span style={{
                marginLeft: '8px', padding: '1px 7px', borderRadius: '10px', background: '#1a1a1a',
                color: '#fff', fontSize: '11px', fontWeight: '500', verticalAlign: '1px'
              }}>
                Author
              </span>
            )}
          </span>
          <span style={{ fontSize: '12px', color: '#999' }}>{formatCommentDate(comment.created_at)}</span>
        </div>
        <p style={{ fontSize: '14px', color: '#333', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{comment.text}</p>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => handleCommentLike(comment.id)}
            disabled={isLiked || commentLikeLoadingId === comment.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              background: 'none', border: 'none', padding: 0,
              fontSize: '12px', color: isLiked ? '#e0245e' : '#888',
              cursor: isLiked ? 'default' : 'pointer'
            }}
            data-testid={`comment-like-btn-${comment.id}`}
          >
            <Heart size={13} fill={isLiked ? '#e0245e' : 'none'} />
            {comment.likes_count > 0 ? comment.likes_count : 'Like'}
          </button>
          <button
            type="button"
            onClick={() => {
              setReplyingToId(replyingToId === comment.id ? null : comment.id);
              setReplyForm({ ...getSavedCommenter(), text: '', website: '' });
            }}
            style={{ background: 'none', border: 'none', padding: 0, fontSize: '12px', color: '#888', cursor: 'pointer' }}
            data-testid={`comment-reply-btn-${comment.id}`}
          >
            Reply
          </button>
        </div>

        {replyingToId === comment.id && (
          <form onSubmit={(e) => handleReplySubmit(e, comment.id)} style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Name"
                value={replyForm.name}
                onChange={(e) => setReplyForm({ ...replyForm, name: e.target.value })}
                required
                style={{ flex: '1 1 160px', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px' }}
              />
              <input
                type="email"
                placeholder="Email (optional, private, for reply alerts)"
                value={replyForm.email}
                onChange={(e) => setReplyForm({ ...replyForm, email: e.target.value })}
                style={{ flex: '1 1 220px', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px' }}
              />
            </div>
            {/* Honeypot — hidden from real visitors, bots that auto-fill every field trip it */}
            <input
              type="text"
              name="website"
              value={replyForm.website}
              onChange={(e) => setReplyForm({ ...replyForm, website: e.target.value })}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
            <textarea
              placeholder={`Reply to ${comment.name}...`}
              value={replyForm.text}
              onChange={(e) => setReplyForm({ ...replyForm, text: e.target.value })}
              required
              rows={3}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '8px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" disabled={replySubmitting} className="btn btn-primary" style={{ fontSize: '13px', padding: '6px 14px' }}>
                {replySubmitting ? 'Posting...' : 'Post Reply'}
              </button>
              <button
                type="button"
                onClick={() => setReplyingToId(null)}
                className="btn btn-secondary"
                style={{ fontSize: '13px', padding: '6px 14px' }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {replies.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {replies.map((reply) => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="public-site">
      {/* Header */}
      <header className="public-header">
        <div className="public-container">
          <Link to="/" className="site-logo">
            <img src="https://daily-obx-cdn.b-cdn.net/newsletter-images/theobxbeachbumlogo.png" alt="the OBX Beach Bum" className="logo-image" />
          </Link>
          <nav className="public-nav">
            <Link to="/" className="nav-link">Stories</Link>
            <Link to="/subscribe" className="nav-link">
              <Mail className="w-4 h-4" style={{ display: 'inline', marginRight: '6px' }} />
              Follow
            </Link>
          </nav>
        </div>
      </header>

      {/* Post Content */}
      <article className="post-view">
        <div className="post-view-container">
          <button
            onClick={() => (location.key === 'default' ? navigate('/') : navigate(-1))}
            className="back-button"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <header className="post-header">
            <h1 className="post-title" data-testid="post-title">{post.title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <time className="post-date" data-testid="post-date">
                {formatDate(post.published_at)}
              </time>
              {!post.image_url && renderLikeButton()}
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="post-featured-image" onContextMenu={(e) => e.preventDefault()}>
              <img src={post.image_url} alt={post.title} draggable="false" />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: post.available_for_purchase ? 'space-between' : 'flex-end',
                flexWrap: 'wrap',
                gap: '12px',
                marginTop: '15px'
              }}>
                {post.available_for_purchase && (
                  <button
                    onClick={() => openPrintModal(post.image_url, post.title)}
                    className="buy-print-btn"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 24px',
                      background: '#1a1a1a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    data-testid="buy-featured-print"
                  >
                    <ShoppingBag size={18} />
                    Buy This Print
                  </button>
                )}
                {renderLikeButton()}
              </div>
            </div>
          )}

          {/* Additional Images */}
          {post.image_urls && post.image_urls.length > 0 && (
            <div className="post-additional-images" style={{ marginTop: '30px' }}>
              {post.image_urls.map((imgUrl, index) => (
                <div key={index} className="post-image-item" style={{ marginBottom: '30px' }} onContextMenu={(e) => e.preventDefault()}>
                  <img
                    src={imgUrl}
                    alt={`${post.title} - Image ${index + 1}`}
                    style={{ width: '100%', borderRadius: '8px' }}
                    draggable="false"
                  />
                  {post.available_for_purchase && (
                    <button 
                      onClick={() => openPrintModal(imgUrl, `${post.title} - Image ${index + 1}`)}
                      className="buy-print-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '15px',
                        padding: '12px 24px',
                        background: '#1a1a1a',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '15px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                      data-testid={`buy-print-${index}`}
                    >
                      <ShoppingBag size={18} />
                      Buy This Print
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="post-content" data-testid="post-content">
            <div className="post-body rich-text-content">
              {(() => {
                // Photos pasted directly into the body text (via "Insert
                // Image") get the same buy button as the featured/additional
                // images — otherwise a photo mentioned inline had no way to
                // be sold at all.
                // "not for sale" in a photo's brackets, e.g. ![not for sale](url),
                // skips its button (a photo used as an ad for the calendar).
                let inlineImageIndex = 0;
                const ContentImage = ({ src, alt }) => {
                  const index = inlineImageIndex++;
                  const notForSale = /not for sale/i.test(alt || '');
                  const altText = (alt || '').replace(/[\s,—-]*not for sale[\s,—-]*/i, ' ').trim();
                  return (
                    <span style={{ display: 'block', margin: '20px 0' }} onContextMenu={(e) => e.preventDefault()}>
                      <img
                        src={src}
                        alt={altText || post.title}
                        style={{ width: '100%', borderRadius: '8px', display: 'block' }}
                        draggable="false"
                        onDragStart={(e) => e.preventDefault()}
                      />
                      {post.available_for_purchase && !notForSale && (
                        <button
                          onClick={() => openPrintModal(src, alt || `${post.title} - Photo ${index + 1}`)}
                          className="buy-print-btn"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '15px',
                            padding: '12px 24px',
                            background: '#1a1a1a',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '15px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                          data-testid={`buy-print-inline-${index}`}
                        >
                          <ShoppingBag size={18} />
                          Buy This Print
                        </button>
                      )}
                    </span>
                  );
                };
                return (
                  <ReactMarkdown components={{ img: ContentImage }}>{post.content}</ReactMarkdown>
                );
              })()}
            </div>
          </div>

          {/* Like + Comment, right where the reader finishes the post — the
              comment section itself sits below the calendar banner and the
              signup box, which readers take for the end of the page. */}
          <div
            data-testid="post-reactions"
            style={{
              margin: '36px 0 8px', padding: '20px 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap'
            }}
          >
            <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '17px', color: '#444', marginRight: '4px' }}>
              What did you think?
            </span>
            {renderLikeButton()}
            <button
              type="button"
              onClick={goToCommentBox}
              data-testid="comment-jump-button"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
                border: '1px solid #ddd', borderRadius: '20px', background: '#fff', color: '#666',
                fontSize: '14px', cursor: 'pointer'
              }}
            >
              <MessageCircle size={16} />
              {comments.length > 0 ? `Comment · ${comments.length}` : 'Comment'}
            </button>
          </div>

          {post.show_calendar_promo && <CalendarPromoBanner />}

          {/* Subscribe CTA */}
          <PostSubscribeForm postPath={location.pathname} />

          {/* Comments */}
          <div id="comments" style={{ marginTop: '50px', paddingTop: '30px', borderTop: '1px solid #eee' }}>
            <h3 style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', marginBottom: '24px'
            }}>
              <MessageCircle size={20} />
              {comments.length > 0 ? `${comments.length} Comment${comments.length === 1 ? '' : 's'}` : 'Comments'}
            </h3>

            <form onSubmit={handleCommentSubmit} style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Name"
                  value={commentForm.name}
                  onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                  required
                  style={{ flex: '1 1 200px', padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '14px' }}
                  data-testid="comment-name-input"
                />
                <input
                  type="email"
                  placeholder="Email (optional, private, for reply alerts)"
                  value={commentForm.email}
                  onChange={(e) => setCommentForm({ ...commentForm, email: e.target.value })}
                  style={{ flex: '1 1 260px', padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '14px' }}
                  data-testid="comment-email-input"
                />
              </div>
              <p style={{ fontSize: '12px', color: '#888', margin: '-4px 0 10px' }}>
                Leave your email and you'll get a note when someone replies. It's never shown or shared.
              </p>
              {/* Honeypot — hidden from real visitors, bots that auto-fill every field trip it */}
              <input
                type="text"
                name="website"
                value={commentForm.website}
                onChange={(e) => setCommentForm({ ...commentForm, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />
              <textarea
                ref={commentBoxRef}
                placeholder="Add a comment..."
                value={commentForm.text}
                onChange={(e) => setCommentForm({ ...commentForm, text: e.target.value })}
                required
                rows={4}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '12px' }}
                data-testid="comment-text-input"
              />
              <button
                type="submit"
                disabled={commentSubmitting}
                className="btn btn-primary"
                data-testid="comment-submit-btn"
              >
                {commentSubmitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>

            {commentsLoading ? (
              <p style={{ color: '#888', fontSize: '14px' }}>Loading comments&hellip;</p>
            ) : comments.length === 0 ? (
              <p style={{ color: '#888', fontSize: '14px' }}>Be the first to comment.</p>
            ) : (
              <div>
                {rootComments.map((comment) => renderComment(comment))}
              </div>
            )}
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="public-footer">
        <div className="public-container">
          <div className="footer-content">
            <p>&copy; {new Date().getFullYear()} the OBX Beach Bum. All rights reserved.</p>
            <div className="footer-links">
              <Link to="/subscribe">Follow</Link>
              <Link to="/support">Support</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Print Selection Modal */}
      {showPrintModal && (
        <div 
          className="print-modal-overlay" 
          onClick={() => setShowPrintModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            boxSizing: 'border-box'
          }}
          data-testid="print-modal-overlay"
        >
          <div
            className="print-modal"
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '16px',
              width: '95%',
              maxWidth: '900px',
              minWidth: 0,
              maxHeight: '90vh',
              overflow: 'hidden',
              overflowY: isMobile ? 'auto' : 'hidden',
              position: 'relative',
              boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
            }}
            data-testid="print-modal"
          >
            <button 
              className="modal-close" 
              onClick={() => setShowPrintModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
              data-testid="close-modal-btn"
            >
              <X size={24} />
            </button>
            
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              height: isMobile ? 'auto' : '100%',
              minHeight: isMobile ? 'auto' : '500px'
            }}>
              {/* Image Preview */}
              <div style={{
                flex: isMobile ? 'none' : '0 0 50%',
                width: isMobile ? '100%' : 'auto',
                minWidth: 0,
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: isMobile ? '16px' : '24px',
                boxSizing: 'border-box'
              }}
              onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  src={selectedImage}
                  alt={selectedImageTitle}
                  style={{
                    maxWidth: '100%',
                    maxHeight: isMobile ? '45vh' : '450px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                  data-testid="modal-image"
                  draggable="false"
                />
              </div>

              {/* Options Panel */}
              <div style={{
                flex: isMobile ? 'none' : '0 0 50%',
                width: isMobile ? '100%' : 'auto',
                minWidth: 0,
                padding: isMobile ? '20px' : '30px',
                background: '#fff',
                overflowY: isMobile ? 'visible' : 'auto',
                boxSizing: 'border-box'
              }}>
                <h2 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: '24px',
                  marginBottom: '8px'
                }}>{selectedImageTitle}</h2>
                <p style={{ color: '#666', marginBottom: '24px' }}>Select your print options below</p>
                
                {/* Print Type Selector */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    marginBottom: '12px', 
                    display: 'block' 
                  }}>Print Type</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {['paper', 'canvas', 'metal'].map(type => (
                      <button
                        key={type}
                        onClick={() => handleTypeChange(type)}
                        style={{
                          padding: '12px 20px',
                          border: '2px solid',
                          borderColor: selectedType === type ? '#1a1a1a' : '#ddd',
                          background: selectedType === type ? '#1a1a1a' : '#fff',
                          color: selectedType === type ? '#fff' : '#333',
                          borderRadius: '8px',
                          fontSize: '15px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        data-testid={`type-${type}`}
                      >
                        {typeNames[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size Selector */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    marginBottom: '12px', 
                    display: 'block' 
                  }}>Size</label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '10px' 
                  }}>
                    {pricing[selectedType] && Object.entries(pricing[selectedType]).map(([size, price]) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        style={{
                          padding: '14px',
                          border: '2px solid',
                          borderColor: selectedSize === size ? '#1a1a1a' : '#ddd',
                          background: selectedSize === size ? '#1a1a1a' : '#fff',
                          color: selectedSize === size ? '#fff' : '#333',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                        data-testid={`size-${size.replace(/\s+/g, '-')}`}
                      >
                        <span style={{ display: 'block', fontWeight: '600', fontSize: '15px' }}>{size}</span>
                        <span style={{ display: 'block', fontSize: '14px', marginTop: '4px', opacity: 0.8 }}>${price}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price & Checkout */}
                <div style={{
                  borderTop: '1px solid #eee',
                  paddingTop: '20px',
                  marginTop: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{ fontSize: '16px', color: '#666' }}>Total:</span>
                    <span style={{ fontSize: '28px', fontWeight: '700' }}>${getPrice()}</span>
                  </div>
                  <Button 
                    onClick={handleCheckout}
                    disabled={checkoutLoading || !selectedSize}
                    style={{
                      width: '100%',
                      padding: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      background: '#1a1a1a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: checkoutLoading || !selectedSize ? 'not-allowed' : 'pointer',
                      opacity: checkoutLoading || !selectedSize ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    data-testid="checkout-btn"
                  >
                    <ShoppingBag size={20} />
                    {checkoutLoading ? 'Processing...' : 'Buy Print'}
                  </Button>
                </div>

                <p style={{
                  fontSize: '13px',
                  color: '#999',
                  textAlign: 'center',
                  marginTop: '16px'
                }}>
                  Shipping calculated at checkout. US shipping only.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostView;